import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDate } from "../lib/utils-app";
import { articleRegion } from "../lib/article-utils";
import Seo from "../components/Seo";
import { Search as SearchIcon, Filter } from "lucide-react";
import { CATEGORIES } from "../lib/utils-app";

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "relevance", label: "Most relevant" },
  { key: "read", label: "Most read" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cat, setCat] = useState(params.get("category") || "");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    const query = params.get("q");
    if (!query && !cat) return;
    setLoading(true);
    const url = `/articles?${query ? `q=${encodeURIComponent(query)}&` : ""}${cat ? `category=${encodeURIComponent(cat)}&` : ""}limit=40`;
    api.get(url).then((r) => setResults(r.data.items)).finally(() => setLoading(false));
  }, [params, cat]);

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (q) next.q = q;
    if (cat) next.category = cat;
    setParams(next);
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-10 md:py-16" data-testid="search-page">
      <Seo
        title={params.get("q") ? `Search · ${params.get("q")}` : "Search"}
        description="Search IndiaODR's arbitration intelligence archive — by keyword, topic, institution or category."
        path={`/search${params.get("q") ? `?q=${params.get("q")}` : ""}`}
        noIndex
      />
      <div className="eyebrow">Search the archive</div>
      <h1 className="font-serif text-[44px] md:text-[56px] mt-2 text-[var(--io-text)]">Find arbitration intelligence</h1>

      <form onSubmit={submit} className="mt-8 flex flex-col md:flex-row gap-3 items-stretch">
        <div className="flex-1 flex items-center gap-3 border border-[var(--io-border)] focus-within:border-[var(--io-gold)] rounded-md px-4 bg-[var(--io-bg-2)]">
          <SearchIcon size={18} className="text-[var(--io-muted)]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search arbitration, ODR, institutions, case law, enforcement…"
            className="flex-1 bg-transparent py-3.5 text-[16px] outline-none text-[var(--io-text)] placeholder:text-[var(--io-muted)]"
            data-testid="search-input"/>
        </div>
        <button className="btn btn-primary" data-testid="search-submit"><SearchIcon size={14}/> Search</button>
      </form>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="search-filters">
        <span className="eyebrow flex items-center gap-1.5 mr-2"><Filter size={12}/> Filters</span>
        <button onClick={() => setCat("")} className={`io-badge ${!cat ? "io-badge-gold" : ""}`} data-testid="filter-all">All topics</button>
        {CATEGORIES.slice(0, 8).map((c) => (
          <button key={c} onClick={() => setCat(c === cat ? "" : c)} className={`io-badge ${cat === c ? "io-badge-gold" : ""}`} data-testid={`filter-cat-${c.replace(/\s+/g,"-").toLowerCase()}`}>{c}</button>
        ))}
        <span className="ml-auto flex items-center gap-2 text-[11px] font-mono text-[var(--io-muted)]">
          <span>Sort</span>
          {SORTS.map((s) => (
            <button key={s.key} onClick={() => setSort(s.key)} className={`io-badge ${sort === s.key ? "io-badge-gold" : ""}`}>{s.label}</button>
          ))}
        </span>
      </div>

      <div className="mt-10 divide-y divide-[var(--io-border)]">
        {loading && <div className="py-10 text-[var(--io-muted)]">Searching intelligence archive…</div>}
        {!loading && (params.get("q") || cat) && results.length === 0 && <div className="py-10 text-[var(--io-muted)]" data-testid="search-empty">No results. Try different keywords or filter combinations.</div>}
        {results.map((a) => (
          <Link key={a.id} to={`/article/${a.slug}`} className="block py-6 group" data-testid={`search-result-${a.slug}`}>
            <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--io-muted)]">
              <span className="text-[var(--io-gold)]">{a.category}</span>
              <span>·</span><span>{articleRegion(a)}</span>
              <span>·</span><span>{fmtDate(a.published_at)}</span>
              <span>·</span><span>{a.read_minutes || 4} min</span>
              <span>·</span><span>{(a.source_refs || []).length} src</span>
            </div>
            <h3 className="font-serif text-[22px] md:text-[26px] mt-1.5 text-[var(--io-text)] group-hover:text-[var(--io-gold)]">{a.headline}</h3>
            <p className="mt-2 text-[14px] text-[var(--io-text-2)] line-clamp-2">{a.summary}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
